import React from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useLabels, usePrefs } from 'ws-scripts/modules/common';
import { useFlags } from 'wsm-common-data';
import DropdownMenu from './DropdownMenu';
import MegaMenu from './MegaMenu';
import NavLink from './NavLink';
import { openMenu, closeAllMenus } from '../actions/menu';
import { setSelected } from '../utils/setSelected';
import { dispatchTrackingEvent } from '../utils/dispatchTrackingEvent';

const MenuItem = ({
	columns,
	deviceType,
	index,
	isSiteWideSearchNavItem,
	label,
	numTopLevelItems,
	pageId,
	searchPosition,
	siteWidePositionFirst,
	siteWidePositionLast,
	subnavItems,
	target,
	url
}) => {
	const labels = useLabels();
	const {
		allowTopLevelLinks,
		dropdownDisplayType,
		enableMegaMenus,
		enableSiteWideSearch,
		homeItemClasses,
		navItemClasses,
		searchButtonIconSize,
		searchButtonLabel,
		searchButtonStyle,
		showDropdownIcon,
		showIcons,
		topLevelLinkClasses,
		'navigation.id': navId
	} = usePrefs();

	const dispatch = useDispatch();
	const flags = useFlags();

	const navsV2 = useSelector((state) => state.widgetData.navsV2);
	const openMenus = useSelector((state) => state.menu.openMenus);
	const currentPath = useSelector((state) => state.currentPage?.currentPath);

	const isHomeNavItem = pageId === 'INDEX';
	const homeIconClasses = ['ddc-icon', 'ddc-icon-home'].join(' ');
	const searchIconClasses = ['ddc-icon', 'ddc-icon-search'].join(' ');

	const isOpen = openMenus?.includes(index);
	const siteWideSearchEnabled = enableSiteWideSearch !== 'disabled';
	const isInventorySearch =
		pageId === 'INVENTORY_SEARCH_FREE_FORM_FRAGMENT' ||
		url === '/navigation-fragments/free-form-search.htm';
	const allowInventorySearch = isInventorySearch && !siteWideSearchEnabled;
	const footer = navId.toLowerCase().includes('footer');

	let menuItemHref = '#';
	if (
		!allowInventorySearch ||
		(!isSiteWideSearchNavItem &&
			(url !== '/404.htm' || url !== '/404.html'))
	) {
		menuItemHref = url?.replaceAll(/\s/g, '');
	}
	const isCurrentPage = !subnavItems?.length
		? Boolean(url === currentPath)
		: false; // account for top level navItems with children but have href assigned
	const isCurrentPageParent =
		isCurrentPage === false ? setSelected(subnavItems, currentPath) : false;

	// disallows simultaneous rendering of inventory search fragment and site-text-search
	if (siteWideSearchEnabled && isInventorySearch) {
		return <></>;
	}

	// disallow rendering of broken navigation links in footer,
	// or if nav-item is top level without children
	if (
		(footer || !subnavItems?.length) &&
		(url === '/404.htm' || url === '/404.html')
	) {
		return <></>;
	}

	const buildInnerMenuItemClasses = () => {
		const innerMenuItemClasses = [];

		if (deviceType === 'MOBILE') {
			innerMenuItemClasses.push(
				'no-follow panel-heading-link panel-toggle collapsed'
			);
		}

		if (isHomeNavItem) {
			innerMenuItemClasses.push('homeLink');
		}

		if (showIcons === 'true' && (allowInventorySearch || isHomeNavItem)) {
			innerMenuItemClasses.push('ddc-nav-icon-link');
		}

		if (isCurrentPage) {
			innerMenuItemClasses.push('currentPage');
		}

		if (isSiteWideSearchNavItem) {
			innerMenuItemClasses.push(
				`btn text-decoration-none btn-no-decoration btn-${searchButtonStyle}`
			);
		}

		if (subnavItems?.length && navsV2) {
			innerMenuItemClasses.push('nav-with-children');
		}

		if (topLevelLinkClasses.toLowerCase() !== 'blank') {
			innerMenuItemClasses.push(topLevelLinkClasses);
		}

		return innerMenuItemClasses.join(' ').trim().trimStart();
	};

	const buildInnerMenuAttributes = () => {
		const innerMenuAttributes = {};
		if (deviceType === 'MOBILE') {
			innerMenuAttributes[
				'data-target'
			] = `#ddc-header-accordion-nav-${index}`;
		}

		if (isSiteWideSearchNavItem) {
			const searchLabel = labels.get(searchButtonLabel);
			const searchTitle = labels.get('SEARCH_THE_WHOLE_SITE');
			innerMenuAttributes['aria-label'] = labels.has(searchLabel)
				? searchLabel
				: searchButtonLabel;
			innerMenuAttributes.title = searchTitle;
			innerMenuAttributes.type = 'button';
			innerMenuAttributes['data-testid'] = `search-${searchPosition}`;
		}

		if (subnavItems?.length) {
			innerMenuAttributes['data-inactive'] = true;
			if (navsV2 && allowTopLevelLinks === 'false') {
				innerMenuAttributes['data-toggle'] = 'dropdown';
			}
			if (!isHomeNavItem) {
				innerMenuAttributes['aria-expanded'] = isOpen;
			}
		}

		return innerMenuAttributes;
	};

	const buildOuterNavItemClasses = () => {
		const outerNavItemClasses = [
			navItemClasses && navItemClasses !== 'blank' ? navItemClasses : ''
		];

		if (isHomeNavItem) {
			outerNavItemClasses.push('home');
			if (homeItemClasses.toLowerCase() !== 'blank') {
				outerNavItemClasses.push(homeItemClasses);
			}
		}

		if (allowInventorySearch) {
			outerNavItemClasses.push('search');
		}

		if (showIcons === 'true' && (allowInventorySearch || isHomeNavItem)) {
			outerNavItemClasses.push('ddc-nav-icon-list-item');
		}

		if (
			(!siteWideSearchEnabled && index === 0) ||
			(siteWideSearchEnabled && siteWidePositionLast && index === 0) ||
			(siteWideSearchEnabled &&
				siteWidePositionFirst &&
				isSiteWideSearchNavItem)
		) {
			outerNavItemClasses.push('nav-first');
		}

		if (
			(!siteWideSearchEnabled && index === numTopLevelItems - 1) ||
			(siteWideSearchEnabled &&
				siteWidePositionFirst &&
				index === numTopLevelItems - 1) ||
			(siteWideSearchEnabled &&
				siteWidePositionLast &&
				isSiteWideSearchNavItem)
		) {
			outerNavItemClasses.push('nav-last');
		}

		if (navsV2 || enableMegaMenus === 'true') {
			outerNavItemClasses.push(
				`dropdown dropdown ${
					isOpen && !isSiteWideSearchNavItem ? 'open' : ''
				}`
			);
		} else {
			outerNavItemClasses.push('nav-children dropdown');
		}

		if (isCurrentPage) {
			outerNavItemClasses.push('currentPage');
		}

		if (isCurrentPageParent) {
			outerNavItemClasses.push('currentPageParent');
		}

		if (!subnavItems?.length) {
			outerNavItemClasses.push('nav-no-children');
		}

		return outerNavItemClasses.join(' ').trim().trimStart();
	};

	const outerClasses = buildOuterNavItemClasses();
	const innerClasses = buildInnerMenuItemClasses();
	const innerAttributes = buildInnerMenuAttributes();

	const toggleDropdown = () => {
		dispatch(closeAllMenus());

		if (typeof window.jQuery === 'function') {
			$('.navbar-nav li.dropdown')
				.eq(index)
				.trigger('hidden.bs.dropdown');
		}

		if (!isOpen && subnavItems.length > 0) {
			dispatch(openMenu(index));
		}
	};

	// This is a reconstruction of the $shouldRender check
	if (flags['enable-no-empty-link-nav-items'] === true) {
		if (!!subnavItems?.length === false && url === false) {
			return <></>;
		}
	}

	const getMenuType = () => {
		if (subnavItems?.length) {
			if (enableMegaMenus === 'true') {
				return <MegaMenu index={index} columns={columns} />;
			} else {
				return <DropdownMenu subnavItems={subnavItems} />;
			}
		} else {
			return <></>;
		}
	};

	const handleClick = (e) => {
		// As a note: This was added to prevent top-level nav-items from navigating out.
		// There is something in cms-web that also does this but we were unable
		// to locate the source and take advantage of it.
		// This may cause issues for us in the future.
		if (subnavItems?.length) {
			e.preventDefault();
		}

		if (isSiteWideSearchNavItem) {
			const event = new Event('ws-site-text-search-open-modal');
			e.preventDefault();
			document.dispatchEvent(event);
		}
	};

	const megaMenuEvents =
		dropdownDisplayType === 'hover'
			? {
					onMouseEnter: () => {
						toggleDropdown();
					}
			  }
			: {
					onClick: (e) => {
						dispatchTrackingEvent(e);
						e.stopPropagation();
						toggleDropdown();
					}
			  };

	const innerAttrs = { ...innerAttributes };
	innerAttrs.onClick = handleClick;

	// managing Non Mega Menu events at a widget level interferes with cms-web inheritance
	const outerElAttrs = enableMegaMenus === 'true' ? megaMenuEvents : () => {};

	if (isCurrentPageParent) {
		outerElAttrs['aria-current'] = 'page';
	}

	outerElAttrs.className = outerClasses;

	const buildNavLinkContent = () => {
		let navLinkContent = '';
		if (showIcons === 'true' && isHomeNavItem) {
			navLinkContent = (
				<>
					<i aria-hidden="true" className={homeIconClasses} />
					<span className="sr-only">{label}</span>
				</>
			);
		} else if (showIcons === 'true' && allowInventorySearch) {
			navLinkContent = (
				<>
					<i
						aria-hidden="true"
						className={`${searchIconClasses} ddc-icon-size-${searchButtonIconSize}`}
					/>
					<span className="sr-only">{label}</span>
				</>
			);
		} else if (isSiteWideSearchNavItem) {
			navLinkContent = <i className={searchIconClasses} />;
		} else if (deviceType === 'MOBILE') {
			navLinkContent = (
				<>
					<i className="ddc-icon ddc-icon-size-small ddc-icon-plus" />
					<span>{label}</span>
				</>
			);
		} else {
			navLinkContent = label;
		}
		return navLinkContent;
	};

	const MenuItemWrapper = (Component) => {
		if (deviceType === 'MOBILE') {
			return (
				<li className="panel panel-default">
					<div className="panel-heading">
						<div className="panel-title">{Component}</div>
					</div>
				</li>
			);
		} else {
			return (
				<li {...outerElAttrs}>
					{Component}
					{getMenuType()}
				</li>
			);
		}
	};

	return MenuItemWrapper(
		<NavLink
			additionalAttrs={innerAttrs}
			classes={innerClasses}
			target={target}
			topLevelNavIndex={index}
			url={menuItemHref}
		>
			<>
				{buildNavLinkContent()}
				{!!subnavItems?.length &&
					showDropdownIcon === 'true' &&
					deviceType !== 'MOBILE' && <b className="caret" />}
			</>
		</NavLink>
	);
};

MenuItem.propTypes = {
	columns: PropTypes.arrayOf.isRequired,
	deviceType: PropTypes.string.isRequired,
	index: PropTypes.string.isRequired,
	isSiteWideSearchNavItem: PropTypes.bool.isRequired,
	label: PropTypes.string.isRequired,
	numTopLevelItems: PropTypes.string.isRequired,
	pageId: PropTypes.string.isRequired,
	searchPosition: PropTypes.string.isRequired,
	siteWidePositionFirst: PropTypes.bool.isRequired,
	siteWidePositionLast: PropTypes.bool.isRequired,
	subnavItems: PropTypes.objectOf({
		id: PropTypes.string.isRequired,
		url: PropTypes.string.isRequired,
		label: PropTypes.string.isRequired,
		navigationItems: PropTypes.array.isRequired
	}),
	target: PropTypes.string.isRequired,
	url: PropTypes.string.isRequired
};

export default MenuItem;
